import React from "react";
import { ClientSelector, init, StorageAdapterLS } from "@opendash/core";
import { getCurrentLanguageSync } from "@opendash/i18n";
import { registerIconPack } from "@opendash/icons";
import { MetaPlugin } from "@opendash/plugin-meta";
import { $parse, ParsePlugin } from "@opendash/plugin-parse";
import dayjs from "dayjs";
import { GlobalStyles } from "./GlobalStyles";
import { FestivalCalcPage } from "./pages/FestivalCalcPage";
import { SchedulePage } from "./pages/SchedulePage";
import { CarpoolPage } from "./pages/CarpoolPage";
import { VolunteerPage } from "./pages/VolunteerPage";


import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Parse from "parse";
import { LocationCustomField } from "./pages/SchedulePage/components/LocationCustomField";
import { EventSegmentsCustomField } from "./pages/FestivalCalcPage/components/EventSegmentsCustomField";
import { TagsCustomField } from "./pages/FestivalCalcPage/components/TagsCustomField";

init("opendash", async (factory) => {
  const clientselector = ClientSelector.getInstance();

  Parse.initialize(clientselector.getParseAppId(), "");
  Parse.serverURL = clientselector.getParseHost();

  registerIconPack(await import("@opendash/icons/dist/fa-regular.json"));
  registerIconPack(await import("@opendash/icons/dist/fa-solid.json"));
  registerIconPack(await import("@opendash/icons/dist/fa-light.json"));

  factory.ui.setLogoImage("/logo-sch.png");
  factory.ui.disableHeaderSourcePicker();
  factory.ui.disableFooter();

  factory.registerGlobalComponent(GlobalStyles, {});

  // Translations
  factory.registerLanguage("en", "English");
  factory.registerLanguage("de", "Deutsch", "en", true);

  factory.registerAntDesignTranslation(
    "en",
    () => import("antd/lib/locale/en_US"),
  );

  factory.registerAntDesignTranslation(
    "de",
    () => import("antd/lib/locale/de_DE"),
  );

  // TODO:
  // factory.registerTranslationResolver(
  //   "de",
  //   "example",
  //   async () => await import("./i18n/de.json"),
  // );

  // factory.registerTranslationResolver(
  //   "en",
  //   "example",
  //   async () => await import("./i18n/en.json"),
  // );

  // Adapter + Plugins
  factory.registerDeviceStorageAdapter(new StorageAdapterLS());

  await factory.use(
    new ParsePlugin({
      authLdapActive: false,
      useLiveQueries: false,
    }),
  );

  await factory.use(new MetaPlugin());

  // Festival Calc page
  factory.registerStaticNavigationGroup({
    id: "festival-group",
    label: "Festival",
    order: 10,
    icon: "fa:beer",
  });

  factory.registerStaticNavigationGroup({
    id: "festival-group-1",
    label: "Festival",
    order: 10,
    icon: "fa:beer",
  });

  factory.registerStaticNavigationGroup({
    id: "festival-group-2",
    label: "Festival",
    order: 11,
    icon: "fa:beer",
  });

  factory.registerStaticNavigationGroup({
    id: "festival-group-3",
    label: "Festival",
    order: 11,
    icon: "fa:beer",
  });

  factory.registerStaticNavigationGroup({
    id: "festival-group-4",
    label: "Festival",
    order: 11,
    icon: "fa:beer",
  });
  
  factory.registerStaticNavigationItem({
    id: "festival-calc",
    group: "festival-group-1",
    label: "Kalkulation",
    order: 1,
    icon: "fa:calculator",
    activeCondition: "/festival/**",
    place: "frontpage",
    routeCondition: "**",
    permission: "festival:can-see-app",
    link: "/festival",
    color: "#f5a623",
  });

  factory.registerStaticNavigationItem({
    id: "festival-schedule",
    group: "festival-group-2",
    label: "Zeitplan",
    order: 10,
    icon: "fa:calendar",
    activeCondition: "/schedule/**",
    place: "frontpage",
    routeCondition: "**",
    permission: "festival:can-see-app",
    link: "/schedule",
    color: "#f5a623",
  });

  factory.registerStaticNavigationItem({
    id: "festival-carpool",
    group: "festival-group-3",
    label: "Mitfahrgelegenheiten",
    order: 20,
    icon: "fa:car",
    activeCondition: "/carpool/**",
    place: "frontpage",
    routeCondition: "**",
    permission: [],
    link: "/carpool",
    color: "#f5a623",
  });

  factory.registerRoute({
    path: "/festival",
    componentSync: FestivalCalcPage,
  });

  factory.registerRoute({
    path: "/schedule",
    componentSync: SchedulePage,
  });

  factory.registerStaticNavigationItem({
    id: "festival-volunteers",
    group: "festival-group-4",
    label: "Helfende",
    order: 30,
    icon: "fa:hands-helping",
    activeCondition: "/volunteers/**",
    place: "frontpage",
    routeCondition: "**",
    permission: [],
    link: "/volunteers",
    color: "#f5a623",
  });

  factory.registerRoute({
    path: "/carpool",
    componentSync: CarpoolPage,
    auth: "both",
  });

  factory.registerRoute({
    path: "/volunteers",
    componentSync: VolunteerPage,
    auth: "both",
  });

  factory.ui.setLogoLink("#")

  // Festival Parse class configs
  $parse.ui.setDefaultView("SCH_FestivalConfig", {
    type: "table",
    showSearch: false,
  });

  $parse.ui.setClassConfig({
    className: "SCH_FestivalConfig",
    titleFields: ["activeTagId"],
    displayFields: [
      "tags",
      "eventSegments",
      "zusagenPercent",
      "aufbau",
      "abbau",
      "locations",
    ],
    createFields: [
      "tags",
      "eventSegments",
      "zusagenPercent",
      "aufbau",
      "abbau",
      "locations",
    ],
    editFields: [
      "tags",
      "eventSegments",
      "zusagenPercent",
      "aufbau",
      "abbau",
      "locations",
    ],
    customFields: {
      locations: (props) =>
        React.createElement(LocationCustomField, { ...props }),
      eventSegments: (props) =>
        React.createElement(EventSegmentsCustomField, { ...props }),
      tags: (props) => React.createElement(TagsCustomField, { ...props }),
    },
  });

  $parse.ui.setDefaultView("SCH_ScheduleItem", {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig({
    className: "SCH_ScheduleItem",
    titleFields: ["title"],
    displayFields: [
      "type",
      "title",
      "startTime",
      "durationMinutes",
      "locationId",
      "capacity",
      "description",
    ],
    createFields: [
      "type",
      "title",
      "startTime",
      "durationMinutes",
      "locationId",
      "capacity",
      "description",
    ],
    editFields: [
      "type",
      "title",
      "startTime",
      "durationMinutes",
      "locationId",
      "capacity",
      "description",
    ],
  });

  factory.registerStaticNavigationItem({
    id: "festival-config-admin",
    group: "festival-group",
    label: "Konfiguration",
    order: 1,
    icon: "fa:wrench",
    activeCondition: "/admin/parse/SCH_FestivalConfig",
    place: "sidebar",
    routeCondition: "/admin/**",
    permission: "festival:can-see-app",
    link: "/admin/parse/SCH_FestivalConfig",
    color: "#f5a623",
  });

  factory.registerStaticNavigationItem({
    id: "festival-schedule-admin",
    group: "festival-group",
    label: "Zeitplan",
    order: 2,
    icon: "fa:calendar",
    activeCondition: "/admin/parse/SCH_ScheduleItem",
    place: "sidebar",
    routeCondition: "/admin/**",
    permission: "festival:can-see-app",
    link: "/admin/parse/SCH_ScheduleItem",
    color: "#f5a623",
  });

  // Class Config

  // $parse.ui.setDefaultView(SIV_Order.className, {
  //   type: "table",
  //   showSearch: true,
  // });

  // $parse.ui.setClassConfig<SIV_Order>({
  //   className: Example.className,
  //   translationPrefix: "example:classes.",
  //   titleFields: ["no"],
  //   displayFields: [
  //     "example"
  //   ],
  //   createFields: [],
  //   editFields: [],
  // });

  // Navigation

  // factory.registerStaticNavigationGroup({
  //   id: "example-group",
  //   label: "Example",
  //   order: 100,
  //   icon: "fa-solid:building-shield",
  // });

  // factory.registerStaticNavigationItem({
  //   id: "example-dashboard",
  //   group: "example-group",
  //   label: "Example",
  //   order: 1,
  //   icon: "fa:clipboard",
  //   activeCondition: "",
  //   place: "frontpage",
  //   routeCondition: "/",
  //   permission: [],
  //   link: "/orders",
  //   color: "#0081C7",
  // });
}).then((app) => {
  console.log("init open.DASH");
  dayjs.locale(getCurrentLanguageSync());
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("Europe/Berlin");
});
